import { useMemo, useState } from "react"
import ReactDOM from "react-dom"
import { useDispatch, useSelector } from "react-redux"

import { createAscReferral } from "../../../../lib/fetchApi"
import { atpProvidersOptions, insideClinicOptions } from "../../../../lib/systemData"
import Button from "../../../common/Button"
import Modal from "../../../common/Modal"
import ModalInnerWrapper from "../../../common/ModalInnerWrapper"
import Backdrop from "../../../common/modal/Backdrop"
import CustomSelect from "../../../common/interactive/CustomSelect"
import DatepickerWithLabel from "../../../common/interactive/DatepickerWithLabel"
import InputBox from "../../../common/interactive/InputBox"
import PannelBody from "../../../fragments/PannelBody"

const referralTypeOptions = [
    { label: "*Surgical Consult Team", value: "*Surgical Consult Team" },
    { label: "*MUA", value: "*MUA" },
]

function formatErrors(errors) {
    if (!errors) {
        return null
    }
    if (typeof errors === "string") {
        return errors
    }
    if (Array.isArray(errors)) {
        return errors.join(" ")
    }
    if (typeof errors === "object") {
        return Object.values(errors).flat().join(" ")
    }

    return "Unable to create ASC referral."
}

const AscReferralModal = ({ closeModal }) => {
    const dispatch = useDispatch()
    const activeCase = useSelector(state => state.case.activeCase)
    const userToken = useSelector(state => state.user.token)

    const providers = useMemo(() => atpProvidersOptions() || [], [])
    const clinics = useMemo(() => insideClinicOptions() || [], [])

    const [referralDate, setReferralDate] = useState("")
    const [providerId, setProviderId] = useState(null)
    const [referralType, setReferralType] = useState(null)
    const [area, setArea] = useState("")
    const [clinicId, setClinicId] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState(null)

    const canSubmit = referralDate && providerId && referralType && clinicId

    const updateSelect = setter => data => {
        setter(data?.data?.value ?? null)
    }
    const updateValue = setter => data => {
        setter(data?.data?.value ?? "")
    }

    const closeModalHandler = () => {
        if (!isLoading) {
            closeModal()
        }
    }

    const submitHandler = async event => {
        event.preventDefault()
        if (!activeCase?.case?.id || !canSubmit) {
            return
        }

        setIsLoading(true)
        setErrorMessage(null)

        const response = await createAscReferral(userToken, activeCase.case.id, {
            referral_date: referralDate,
            provider_id: providerId,
            referral_type: referralType,
            area,
            clinic_id: clinicId,
        }, dispatch)

        setIsLoading(false)

        if (response?.errorMessage) {
            setErrorMessage(formatErrors(response.errorMessage))
            return
        }

        closeModal()
    }

    return <>
        {ReactDOM.createPortal(
            <Backdrop hide={closeModalHandler} />,
            document.getElementById("backdrop-root")
        )}
        {ReactDOM.createPortal(
            <Modal>
                <ModalInnerWrapper closeModal={closeModalHandler} modalTitle={"ASC Referral"} setWidth={460}>
                    <PannelBody>
                        <form onSubmit={submitHandler}>
                            <div className="row">
                                <DatepickerWithLabel
                                    label="Referral Date"
                                    defaultDate={referralDate}
                                    action={updateValue(setReferralDate)}
                                    identifier={{ type: "case_services" }}
                                    required={true}
                                />
                            </div>
                            <div className="row">
                                <CustomSelect
                                    label="Provider"
                                    optionsData={providers}
                                    preSelectedValue={providerId}
                                    action={updateSelect(setProviderId)}
                                    required={true}
                                />
                            </div>
                            <div className="row">
                                <CustomSelect
                                    label="Referral Type"
                                    optionsData={referralTypeOptions}
                                    preSelectedValue={referralType}
                                    action={updateSelect(setReferralType)}
                                    required={true}
                                />
                            </div>
                            <div className="row">
                                <InputBox
                                    label="Area"
                                    currentValue={area}
                                    action={updateValue(setArea)}
                                    identifier={{ type: "case_services" }}
                                />
                            </div>
                            <div className="row">
                                <CustomSelect
                                    label="Clinic"
                                    optionsData={clinics}
                                    preSelectedValue={clinicId}
                                    action={updateSelect(setClinicId)}
                                    required={true}
                                />
                            </div>
                            {errorMessage && <div className="row"><span className="danger">{errorMessage}</span></div>}
                            <div className="row flex-container">
                                <Button type="button" btnStyle="btn_light_grey" click={closeModalHandler}>Cancel</Button>
                                <Button type="submit" isLoading={isLoading} isDisabled={!canSubmit}>Save</Button>
                            </div>
                        </form>
                    </PannelBody>
                </ModalInnerWrapper>
            </Modal>,
            document.getElementById("overlay-root")
        )}
    </>
}

export default AscReferralModal
